// 图片读写与"清理"：读取（含 EXIF 方向）、裁剪、缩放、温和的白平衡/曝光校正

import { clamp } from "./geometry";

export type AnyCanvas = HTMLCanvasElement;

export function newCanvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  return c;
}

export function ctx2d(c: HTMLCanvasElement): CanvasRenderingContext2D {
  return c.getContext("2d", { willReadFrequently: true })!;
}

export async function blobToCanvas(blob: Blob, maxSide = Number.POSITIVE_INFINITY): Promise<HTMLCanvasElement> {
  const bmp = await decode(blob);
  const s = Math.min(1, maxSide / Math.max(bmp.width, bmp.height));
  const c = newCanvas(Math.round(bmp.width * s), Math.round(bmp.height * s));
  const g = ctx2d(c);
  g.imageSmoothingQuality = "high";
  g.drawImage(bmp, 0, 0, c.width, c.height);
  if ("close" in bmp) bmp.close();
  return c;
}

async function decode(blob: Blob): Promise<ImageBitmap | HTMLImageElement> {
  try {
    return await createImageBitmap(blob, { imageOrientation: "from-image" });
  } catch {
    // 个别浏览器 createImageBitmap 不认某些格式，退回 <img>
    const url = URL.createObjectURL(blob);
    try {
      const img = new Image();
      img.decoding = "async";
      img.src = url;
      await img.decode();
      return img;
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}

export function canvasToBlob(c: HTMLCanvasElement, type = "image/jpeg", quality = 0.9): Promise<Blob> {
  return new Promise((resolve, reject) =>
    c.toBlob((b) => (b ? resolve(b) : reject(new Error("图片编码失败"))), type, quality),
  );
}

export function crop(
  src: HTMLCanvasElement,
  rect: { x: number; y: number; w: number; h: number },
  maxW: number,
  maxH: number,
): HTMLCanvasElement {
  const s = Math.min(1, maxW / rect.w, maxH / rect.h);
  const c = newCanvas(Math.round(rect.w * s), Math.round(rect.h * s));
  const g = ctx2d(c);
  g.imageSmoothingQuality = "high";
  g.drawImage(src, rect.x, rect.y, rect.w, rect.h, 0, 0, c.width, c.height);
  return c;
}

/** 按 cover 方式把 src 画到 w×h（结果图和原图比例略有差异时使用） */
export function coverTo(src: CanvasImageSource & { width: number; height: number }, w: number, h: number) {
  const c = newCanvas(w, h);
  const g = ctx2d(c);
  const s = Math.max(w / src.width, h / src.height);
  const dw = src.width * s;
  const dh = src.height * s;
  g.imageSmoothingQuality = "high";
  g.drawImage(src, (w - dw) / 2, (h - dh) / 2, dw, dh);
  return c;
}

/**
 * 温和的清理：灰度世界白平衡（只纠正 30%）+ 按脸部亮度做轻微曝光补偿。
 * 只动整体色调，不改变任何像素位置。
 */
export function autoClean(c: HTMLCanvasElement, skinMask?: { data: Uint8Array; w: number; h: number }): void {
  const g = ctx2d(c);
  const img = g.getImageData(0, 0, c.width, c.height);
  const d = img.data;
  let r = 0;
  let gg = 0;
  let b = 0;
  let n = 0;
  const step = 4 * 7;
  for (let i = 0; i < d.length; i += step) {
    const y = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
    if (y < 20 || y > 240) continue; // 忽略过暗过亮
    r += d[i];
    gg += d[i + 1];
    b += d[i + 2];
    n++;
  }
  if (!n) return;
  r /= n;
  gg /= n;
  b /= n;
  const gray = (r + gg + b) / 3;
  const strength = 0.3;
  const kr = clamp(1 + (gray / r - 1) * strength, 0.9, 1.1);
  const kg = clamp(1 + (gray / gg - 1) * strength, 0.9, 1.1);
  const kb = clamp(1 + (gray / b - 1) * strength, 0.9, 1.1);

  // 曝光：脸部皮肤平均亮度偏暗时提亮（伽马），最多提一点点
  let gamma = 1;
  if (skinMask) {
    let sum = 0;
    let cnt = 0;
    const sx = skinMask.w / c.width;
    const sy = skinMask.h / c.height;
    for (let y = 0; y < c.height; y += 4) {
      for (let x = 0; x < c.width; x += 4) {
        const m = skinMask.data[Math.floor(y * sy) * skinMask.w + Math.floor(x * sx)];
        if (m < 160) continue;
        const i = (y * c.width + x) * 4;
        sum += 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
        cnt++;
      }
    }
    if (cnt > 50) {
      const mean = sum / cnt / 255;
      const target = 0.6;
      if (mean < target) gamma = clamp(Math.log(target) / Math.log(Math.max(mean, 0.05)), 0.8, 1);
      gamma = 1 - (1 - gamma) * 0.5; // 只补一半，保持自然
    }
  }

  const lut = (k: number) => {
    const t = new Uint8ClampedArray(256);
    for (let v = 0; v < 256; v++) t[v] = 255 * (Math.min(255, v * k) / 255) ** gamma;
    return t;
  };
  const lr = lut(kr);
  const lg = lut(kg);
  const lb = lut(kb);
  for (let i = 0; i < d.length; i += 4) {
    d[i] = lr[d[i]];
    d[i + 1] = lg[d[i + 1]];
    d[i + 2] = lb[d[i + 2]];
  }
  g.putImageData(img, 0, 0);
}

/** 把 0-255 单通道掩码缩放成 w×h 的 alpha 画布（双线性插值，边缘自然柔和） */
export function maskToCanvas(mask: Uint8Array, mw: number, mh: number, w: number, h: number): HTMLCanvasElement {
  const small = newCanvas(mw, mh);
  const g = ctx2d(small);
  const img = g.createImageData(mw, mh);
  for (let i = 0; i < mask.length; i++) {
    img.data[i * 4 + 3] = mask[i];
  }
  g.putImageData(img, 0, 0);
  if (mw === w && mh === h) return small;
  const big = newCanvas(w, h);
  const bg = ctx2d(big);
  bg.imageSmoothingEnabled = true;
  bg.imageSmoothingQuality = "high";
  bg.drawImage(small, 0, 0, w, h);
  return big;
}

/** 把 alpha 画布读回 w×h 的 Uint8 掩码 */
export function canvasAlpha(c: HTMLCanvasElement): Uint8Array {
  const d = ctx2d(c).getImageData(0, 0, c.width, c.height).data;
  const out = new Uint8Array(c.width * c.height);
  for (let i = 0; i < out.length; i++) out[i] = d[i * 4 + 3];
  return out;
}

/** 在 mask 分辨率下采样另一张不同分辨率的掩码 */
export function resampleMask(src: Uint8Array, sw: number, sh: number, dw: number, dh: number): Uint8Array {
  if (sw === dw && sh === dh) return src;
  const out = new Uint8Array(dw * dh);
  for (let y = 0; y < dh; y++) {
    const fy = ((y + 0.5) * sh) / dh - 0.5;
    const y0 = clamp(Math.floor(fy), 0, sh - 1);
    const y1 = clamp(y0 + 1, 0, sh - 1);
    const wy = clamp(fy - y0, 0, 1);
    for (let x = 0; x < dw; x++) {
      const fx = ((x + 0.5) * sw) / dw - 0.5;
      const x0 = clamp(Math.floor(fx), 0, sw - 1);
      const x1 = clamp(x0 + 1, 0, sw - 1);
      const wx = clamp(fx - x0, 0, 1);
      const top = src[y0 * sw + x0] * (1 - wx) + src[y0 * sw + x1] * wx;
      const bot = src[y1 * sw + x0] * (1 - wx) + src[y1 * sw + x1] * wx;
      out[y * dw + x] = top * (1 - wy) + bot * wy + 0.5;
    }
  }
  return out;
}
